import type { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';

import type { Drug } from '../components/drug-search';
import type { InteractionCheckResult } from '../types';
import {
  type CheckInteractionsResponse,
  type InteractionCacheRow,
  cacheRowToApiResult,
  getPairKey,
  mapApiResultToFrontend,
  mergeResults,
} from './interaction-check-mappers';

/** Subset of MonitoringService used by the session (avoids extra dependency). */
interface Monitoring {
  captureException(error: Error, extra?: Record<string, unknown>): unknown;
}

// Constants
const MIN_DISPLAY_MS = 3000;
const MAX_DISPLAY_MS = 5000;
const EXPECTED_TIME_MS = 40_000;
const MAX_WAIT_MS = 70_000;
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2_000;
const POLL_INTERVAL_MS = 5_000;
const PROGRESS_TICK_MS = 100;

// Types
export type SessionPhase =
  | 'idle'
  | 'fetching'
  | 'listening'
  | 'polling'
  /** All results were cached; holding them back for a minimum display time */
  | 'delaying'
  | 'finishing'
  | 'done'
  | 'error'
  | 'timeout';

export interface CheckOptions {
  /** Skip the minimum display time when results arrive via Realtime/polling */
  skipDelay?: boolean;
  /**
   * When every pair is already cached, keep the progress animation on screen
   * for at least this long before showing results (the "fake loading" beat
   * used by the English demo). Ignored when not all pairs are cached.
   */
  minDisplayMs?: number;
}

export interface InteractionProgress {
  total: number;
  percentage: number;
}

export interface SessionSnapshot {
  phase: SessionPhase;
  results: InteractionCheckResult[] | null;
  progress: InteractionProgress | null;
  errorKey: string | null;
  hasTimedOut: boolean;
  isLoading: boolean;
}

export type OnSnapshotChange = (snapshot: SessionSnapshot) => void;

/**
 * Manages the full lifecycle of an interaction check:
 *
 *   idle → fetching → listening / polling → finishing → done
 *                                        ↘ error / timeout
 *
 * No React dependency — the hook is a thin wrapper that maps
 * snapshot changes to `useState`.
 */
export class InteractionCheckSession {
  private supabase: SupabaseClient;
  private monitoring: Monitoring;
  private onChange: OnSnapshotChange;
  /** Current UI language, sent as `x-lang` so the API can serve the English demo data */
  private language: string | undefined;

  // Phase
  private phase: SessionPhase = 'idle';

  // Drug context
  private drugsMap = new Map<string, Drug>();
  private drugIds: string[] = [];
  private allPairKeys = new Set<string>();

  // Result tracking
  private resultsMap = new Map<string, InteractionCheckResult>();
  private pendingPairs = new Set<string>();
  private totalPairs = 0;

  // Timing
  private startTime = 0;
  private minDisplayTime = 0;
  private skipDelay = false;
  private expectedTimeMs = EXPECTED_TIME_MS;

  // Timers / subscriptions
  private channel: RealtimeChannel | null = null;
  private timeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private progressTimer: ReturnType<typeof setInterval> | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  // Promise resolution for the check() return value
  private resolveCheck: ((r: InteractionCheckResult[]) => void) | null = null;

  constructor(
    supabase: SupabaseClient,
    monitoring: Monitoring,
    onChange: OnSnapshotChange,
  ) {
    this.supabase = supabase;
    this.monitoring = monitoring;
    this.onChange = onChange;
  }

  // -- Public API -------------------------------------------------------------

  /** Set the UI language forwarded to the API (`x-lang`). */
  setLanguage(language: string | undefined): void {
    this.language = language;
  }

  /** Start a check. Resolves when done, timed out, cancelled, or errored. */
  async check(
    drugs: Drug[],
    options?: CheckOptions,
  ): Promise<InteractionCheckResult[]> {
    // Resolve any pending previous check before tearing it down
    this.resolveAndClear(this.currentResults());
    this.cleanup();
    this.resetState();

    if (drugs.length < 2) {
      this.emit();
      return [];
    }

    this.skipDelay = options?.skipDelay ?? false;
    this.totalPairs = (drugs.length * (drugs.length - 1)) / 2;
    this.drugsMap = new Map(drugs.map((d) => [d.id, d]));
    this.drugIds = drugs.map((d) => d.id);

    for (let i = 0; i < drugs.length; i++) {
      for (let j = i + 1; j < drugs.length; j++) {
        this.allPairKeys.add(getPairKey(drugs[i]!.id, drugs[j]!.id));
      }
    }
    this.pendingPairs = new Set(this.allPairKeys);

    // Show spinner only (no progress bar) while checking cache
    this.setPhase('fetching');

    try {
      const {
        results: initial,
        allDone,
        failedPairKeys,
      } = await this.fetchStatus({
        logCheck: true,
      });
      mergeResults(initial, this.resultsMap, this.pendingPairs);
      this.removeFailedPairs(failedPairKeys);

      if (allDone) {
        // Everything was cached. Optionally hold the results back for a
        // minimum display time with a progress animation sized to that time.
        // The wait happens in the non-terminal 'delaying' phase so the
        // progress ticks keep emitting and cancel()/reset() still work.
        if (options?.minDisplayMs && options.minDisplayMs > 0) {
          this.startTime = Date.now();
          this.minDisplayTime = options.minDisplayMs;
          this.expectedTimeMs = options.minDisplayMs;
          this.skipDelay = false;
          this.startProgressAnimation();
          this.setPhase('delaying');

          await new Promise<void>((r) => setTimeout(r, options.minDisplayMs));

          // Cancelled, reset or superseded while waiting — that call already
          // emitted its own snapshot; return whatever is left.
          if (this.phase !== 'delaying') return this.currentResults();
        }

        const final = this.currentResults();
        await this.finish(final);
        return final;
      }

      // Cache didn't have everything — start progress animation now
      this.startTime = Date.now();
      this.minDisplayTime = this.skipDelay
        ? 0
        : MIN_DISPLAY_MS + Math.random() * (MAX_DISPLAY_MS - MIN_DISPLAY_MS);
      this.startProgressAnimation();

      this.emit(); // show intermediate results

      return new Promise<InteractionCheckResult[]>((resolve) => {
        this.resolveCheck = resolve;
        this.startTimeout();
        this.subscribe();
      });
    } catch (err) {
      const key =
        (err as Error & { errorKey?: string }).errorKey ?? 'apiErrors.generic';
      this.cleanup();
      this.phase = 'error';
      this.emit(key);

      this.monitoring.captureException(
        err instanceof Error ? err : new Error(String(err)),
        {
          phase: 'fetching',
          errorKey: key,
          drugCount: this.drugIds.length,
          totalPairs: this.totalPairs,
        },
      );

      throw err;
    }
  }

  /**
   * Read-only cache probe: hits /api/interactions/check to see if all pairs
   * are already cached.  Does NOT start Realtime, polling, or processing.
   * Returns `{ results, allDone }`.
   */
  async probeCache(
    drugs: Drug[],
  ): Promise<{ results: InteractionCheckResult[]; allDone: boolean }> {
    this.resolveAndClear(this.currentResults());
    this.cleanup();
    this.resetState();

    if (drugs.length < 2) {
      this.emit();
      return { results: [], allDone: true };
    }

    this.totalPairs = (drugs.length * (drugs.length - 1)) / 2;
    this.drugsMap = new Map(drugs.map((d) => [d.id, d]));
    this.drugIds = drugs.map((d) => d.id);

    for (let i = 0; i < drugs.length; i++) {
      for (let j = i + 1; j < drugs.length; j++) {
        this.allPairKeys.add(getPairKey(drugs[i]!.id, drugs[j]!.id));
      }
    }
    this.pendingPairs = new Set(this.allPairKeys);

    const { results, allDone, failedPairKeys } = await this.fetchStatus();
    mergeResults(results, this.resultsMap, this.pendingPairs);
    this.removeFailedPairs(failedPairKeys);

    if (allDone) {
      const final = this.currentResults();
      this.phase = 'done';
      this.onChange({
        phase: 'done',
        results: final,
        progress: { total: this.totalPairs, percentage: 100 },
        errorKey: null,
        hasTimedOut: false,
        isLoading: false,
      });
      return { results: final, allDone: true };
    }

    this.resetState();
    this.emit();
    return { results: [], allDone: false };
  }

  /** Cancel — keeps partial results visible. */
  cancel(): void {
    if (this.isDone()) return;
    const partial = this.currentResults();
    this.cleanup();
    this.phase = 'done';
    this.emit();
    this.resolveAndClear(partial);
  }

  /** Full reset to idle. */
  reset(): void {
    this.resolveAndClear([]);
    this.cleanup();
    this.resetState();
    this.emit();
  }

  /** Tear down on unmount. */
  destroy(): void {
    this.resolveAndClear([]);
    this.cleanup();
  }

  // -- Phase ------------------------------------------------------------------

  /** Terminal = no more state transitions allowed. */
  private isDone(): boolean {
    return (
      this.phase === 'done' ||
      this.phase === 'error' ||
      this.phase === 'timeout' ||
      this.phase === 'finishing'
    );
  }

  private setPhase(phase: SessionPhase): void {
    this.phase = phase;
    this.emit();
  }

  // -- Snapshot ----------------------------------------------------------------

  private snapshot(errorKey: string | null = null): SessionSnapshot {
    return {
      phase: this.phase,
      results: this.resultsMap.size > 0 ? this.currentResults() : null,
      progress:
        this.phase === 'idle'
          ? null
          : this.phase === 'fetching'
            ? { total: this.totalPairs, percentage: 0 }
            : { total: this.totalPairs, percentage: this.currentPercentage() },
      errorKey,
      hasTimedOut: this.phase === 'timeout',
      isLoading:
        this.phase === 'fetching' ||
        this.phase === 'listening' ||
        this.phase === 'polling' ||
        this.phase === 'delaying' ||
        this.phase === 'finishing',
    };
  }

  private emit(errorKey: string | null = null): void {
    this.onChange(this.snapshot(errorKey));
  }

  // -- Fetch ------------------------------------------------------------------

  private async fetchStatus(options?: { logCheck?: boolean }): Promise<{
    results: InteractionCheckResult[];
    allDone: boolean;
    failedPairKeys: string[];
  }> {
    const language = this.language;
    const response = await fetch('/api/interactions/check', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(language ? { 'x-lang': language } : {}),
      },
      body: JSON.stringify({
        drugIds: this.drugIds,
        ...(options?.logCheck && { logCheck: true }),
      }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      const error = new Error(data.errorKey ?? 'apiErrors.generic');
      (error as Error & { errorKey?: string }).errorKey =
        data.errorKey ?? 'apiErrors.generic';
      throw error;
    }

    const data: CheckInteractionsResponse = await response.json();
    const mapped: InteractionCheckResult[] = [];
    const failedPairKeys: string[] = [];

    for (const pair of data.pairs) {
      if (pair.status === 'success' && pair.result) {
        try {
          mapped.push(mapApiResultToFrontend(pair.result, this.drugsMap));
        } catch (e) {
          console.error('Failed to map result:', e);
        }
      } else if (pair.status === 'failed') {
        failedPairKeys.push(getPairKey(pair.drug1Id, pair.drug2Id));
      }
    }

    return { results: mapped, allDone: data.allDone, failedPairKeys };
  }

  // -- Realtime subscription --------------------------------------------------

  private subscribe(retryCount = 0): void {
    if (this.isDone()) return;
    if (this.pendingPairs.size === 0 && retryCount > 0) return;

    this.setPhase('listening');

    const channel = this.supabase
      .channel(`interaction-check-${retryCount}`)
      .on<InteractionCacheRow>(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'interaction_cache' },
        (payload) => this.handlePayload(payload),
      )
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await this.onSubscribed();
        } else if (
          status === 'CHANNEL_ERROR' ||
          status === 'TIMED_OUT' ||
          status === 'CLOSED'
        ) {
          this.onChannelFailure(channel, status, retryCount);
        }
      });

    this.channel = channel;
  }

  private handlePayload(payload: { new: unknown }): void {
    if (this.isDone()) return;

    const row = payload.new as InteractionCacheRow;
    if (!row?.drug1_id || !row?.drug2_id) return;

    const key = getPairKey(row.drug1_id, row.drug2_id);
    if (!this.allPairKeys.has(key)) return;
    if (row.processing_status !== 'success') return;
    if (this.resultsMap.has(key)) return;

    try {
      const result = mapApiResultToFrontend(
        cacheRowToApiResult(row),
        this.drugsMap,
      );
      this.resultsMap.set(key, result);
      this.pendingPairs.delete(key);
      this.emit();
      this.tryFinish();
    } catch (e) {
      console.error('Failed to process realtime update:', e);
    }
  }

  /** Re-fetch after SUBSCRIBED to close the subscription gap. */
  private async onSubscribed(): Promise<void> {
    if (this.isDone()) return;
    try {
      const { results, allDone, failedPairKeys } = await this.fetchStatus();
      const merged = mergeResults(results, this.resultsMap, this.pendingPairs);
      const removed = this.removeFailedPairs(failedPairKeys);
      if (merged || removed) {
        this.emit();
      }
      if (allDone && this.pendingPairs.size === 0) this.tryFinish();
    } catch {
      // Non-fatal: Realtime will still deliver future events
    }
  }

  private onChannelFailure(
    channel: RealtimeChannel,
    status: string,
    retryCount: number,
  ): void {
    if (this.isDone()) return;

    console.warn(
      `Realtime channel ${status} (attempt ${retryCount + 1}/${MAX_RETRIES + 1})`,
    );

    // removeChannel() transitions to CLOSED which re-fires this callback
    // synchronously — skip it when already CLOSED to prevent infinite recursion.
    if (status !== 'CLOSED') {
      void this.supabase.removeChannel(channel);
    }
    if (this.channel === channel) this.channel = null;

    if (retryCount < MAX_RETRIES) {
      this.retryTimer = setTimeout(
        () => this.subscribe(retryCount + 1),
        RETRY_DELAY_MS,
      );
    } else {
      console.warn(
        'Realtime failed after all retries, falling back to polling',
      );
      this.startPolling();
    }
  }

  // -- Polling fallback -------------------------------------------------------

  private startPolling(): void {
    if (this.isDone()) return;
    this.setPhase('polling');

    this.pollTimer = setInterval(async () => {
      if (this.isDone()) return;
      try {
        const { results, allDone, failedPairKeys } = await this.fetchStatus();
        const merged = mergeResults(
          results,
          this.resultsMap,
          this.pendingPairs,
        );
        const removed = this.removeFailedPairs(failedPairKeys);
        if (merged || removed) {
          this.emit();
        }
        if (allDone && this.pendingPairs.size === 0) this.tryFinish();
      } catch {
        // Will retry on next interval
      }
    }, POLL_INTERVAL_MS);
  }

  // -- Completion -------------------------------------------------------------

  private tryFinish(): void {
    if (this.isDone() || this.pendingPairs.size > 0) return;
    void this.finish(this.currentResults());
  }

  private async finish(final: InteractionCheckResult[]): Promise<void> {
    if (this.isDone()) return;
    this.phase = 'finishing'; // now isDone() → true, blocks concurrent entry

    // Clear timeout immediately so it can't fire during the display delay
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
    }

    // Minimum display time for smooth UX
    if (!this.skipDelay) {
      const remaining = this.minDisplayTime - (Date.now() - this.startTime);
      if (remaining > 0) {
        await new Promise<void>((r) => setTimeout(r, remaining));
      }
    }

    // Could have been cancelled/destroyed during the await
    if (this.phase !== 'finishing') return;

    this.cleanup();
    this.phase = 'done';

    // All pairs failed (e.g. missing ChPL extraction) — show error
    const allFailed = final.length === 0 && this.totalPairs > 0;

    this.onChange({
      phase: 'done',
      results: final,
      progress: { total: this.totalPairs, percentage: 100 },
      errorKey: allFailed ? 'apiErrors.generic' : null,
      hasTimedOut: false,
      isLoading: false,
    });
    this.resolveAndClear(final);
  }

  // -- Timeout ----------------------------------------------------------------

  private startTimeout(): void {
    this.timeoutTimer = setTimeout(async () => {
      if (this.isDone()) return;

      // One final fetch before giving up — the data may already be there
      // but Realtime silently failed to deliver the events.
      try {
        const { results, allDone, failedPairKeys } = await this.fetchStatus();

        if (allDone) {
          mergeResults(results, this.resultsMap, this.pendingPairs);
          this.removeFailedPairs(failedPairKeys);
          await this.finish(this.currentResults());
          return;
        }
      } catch {
        // Ignore — fall through to show timeout error
      }

      // Could have finished via Realtime during the fetch, or via finish() above
      if (this.isDone()) return;

      this.monitoring.captureException(
        new Error('Interaction check timed out'),
        {
          phase: 'timeout',
          drugCount: this.drugIds.length,
          totalPairs: this.totalPairs,
          completedPairs: this.resultsMap.size,
          pendingPairs: this.pendingPairs.size,
          elapsedMs: Date.now() - this.startTime,
        },
      );

      this.cleanup();
      this.phase = 'timeout';
      this.onChange({
        phase: 'timeout',
        results: null,
        progress: { total: this.totalPairs, percentage: 0 },
        errorKey: 'apiErrors.generic',
        hasTimedOut: true,
        isLoading: false,
      });
      this.resolveAndClear([]);
    }, MAX_WAIT_MS);
  }

  // -- Progress ---------------------------------------------------------------

  private startProgressAnimation(): void {
    this.progressTimer = setInterval(() => {
      if (this.isDone()) return;
      this.emit();
    }, PROGRESS_TICK_MS);
  }

  private currentPercentage(): number {
    if (this.phase === 'done') return 100;
    return Math.min(
      Math.round(((Date.now() - this.startTime) / this.expectedTimeMs) * 100),
      95,
    );
  }

  // -- Helpers ----------------------------------------------------------------

  /** Remove failed pairs from pendingPairs (they'll never succeed). */
  private removeFailedPairs(keys: string[]): boolean {
    let removed = false;
    for (const key of keys) {
      if (this.pendingPairs.delete(key)) {
        removed = true;
      }
    }
    return removed;
  }

  private currentResults(): InteractionCheckResult[] {
    return Array.from(this.resultsMap.values());
  }

  private resolveAndClear(results: InteractionCheckResult[]): void {
    this.resolveCheck?.(results);
    this.resolveCheck = null;
  }

  private cleanup(): void {
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
    }
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.channel) {
      void this.supabase.removeChannel(this.channel);
      this.channel = null;
    }
  }

  private resetState(): void {
    this.phase = 'idle';
    this.drugsMap = new Map();
    this.drugIds = [];
    this.allPairKeys = new Set();
    this.resultsMap = new Map();
    this.pendingPairs = new Set();
    this.totalPairs = 0;
    this.startTime = 0;
    this.minDisplayTime = 0;
    this.skipDelay = false;
    this.expectedTimeMs = EXPECTED_TIME_MS;
    this.resolveCheck = null;
  }
}
